/**
 * 富の神 (Tomi-no-Kami) Trading Dashboard
 *
 * Real-time futures trading dashboard connected to Coinbase.
 * Panels: Status, Balance, Positions, Trade History, Order Entry
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Card,
  CardContent,
  Grid,
  Typography,
  Chip,
  Button,
  Alert,
  CircularProgress,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  LinearProgress,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Tooltip,
  Slider,
} from '@mui/material';
import {
  MonitorHeart,
  TrendingUp,
  TrendingDown,
  Refresh,
  AccountBalance,
  Circle,
  Send,
  History,
  CurrencyBitcoin,
  Warning,
  CheckCircle,
  Cancel,
  ShowChart,
  Analytics,
  Forum,
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import tomiService, {
  TomiStatus,
  FuturesPosition,
  TradeData,
  OrderRequest,
  DcaAnalysisResult,
} from '../services/tomiService';
import { createCoinbaseDatafeed } from '../utils/tradingViewDatafeed';
import AgentChatPanel from '../components/AgentChatPanel';

// ─── Helpers ────────────────────────────────────────────

interface TabPanelProps {
  children: React.ReactNode;
  value: number;
  index: number;
}

function TabPanel({ children, value, index }: TabPanelProps) {
  return value === index ? <Box sx={{ pt: 3 }}>{children}</Box> : null;
}

function StatusDot({ ok }: { ok: boolean }) {
  return (
    <Circle
      sx={{
        fontSize: 12,
        color: ok ? '#4caf50' : '#f44336',
        mr: 1,
        animation: ok ? undefined : 'pulse 1.5s infinite',
        '@keyframes pulse': { '0%, 100%': { opacity: 1 }, '50%': { opacity: 0.4 } },
      }}
    />
  );
}

function formatUsd(value: string | number | null | undefined): string {
  if (value == null) return '$0.00';
  const num = typeof value === 'string' ? parseFloat(value) : value;
  if (isNaN(num)) return '$0.00';
  return num.toLocaleString('en-US', { style: 'currency', currency: 'USD' });
}

function pnlColor(value: string | number | null | undefined): string {
  if (value == null) return 'inherit';
  const num = typeof value === 'string' ? parseFloat(value) : value;
  if (num > 0) return '#4caf50';
  if (num < 0) return '#f44336';
  return 'inherit';
}

// ─── Futures Product List ───────────────────────────────

const FUTURES_PRODUCTS = [
  'BIT-28MAR25-CDE',
  'BTC-USD',
  'ETH-USD',
  'SOL-USD',
  'DOGE-USD',
  'AVAX-USD',
  'LINK-USD',
  'ADA-USD',
  'DOT-USD',
  'MATIC-USD',
];

// ─── Main Component ─────────────────────────────────────

export default function TomiDashboardPage() {
  const theme = useTheme();

  // State
  const [tab, setTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState<TomiStatus | null>(null);
  const [positions, setPositions] = useState<FuturesPosition[]>([]);
  const [tradeData, setTradeData] = useState<TradeData | null>(null);
  const [orderDialogOpen, setOrderDialogOpen] = useState(false);
  const [resetDialogOpen, setResetDialogOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // DCA Strategy Analysis
  const [dcaResult, setDcaResult] = useState<DcaAnalysisResult | null>(null);
  const [dcaLoading, setDcaLoading] = useState(false);
  const [dcaForm, setDcaForm] = useState({
    capital: 10000,
    target_price: 145000,
    current_price: 70000,
    contracts_per_tranche: 1,
    tranches: 20,
    product_id: 'BTC-USD',
  });

  // TradingView chart
  const chartContainerRef = useRef<HTMLDivElement | null>(null);
  const tvWidgetRef = useRef<any>(null);

  // Order form
  const [orderForm, setOrderForm] = useState<OrderRequest>({
    product_id: 'BTC-USD',
    side: 'BUY',
    size: '',
    order_type: 'market',
    limit_price: '',
    leverage: '3',
  });

  // ─── Data Fetching ───────────────────────────────────

  const fetchAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [statusRes, posRes, tradeRes] = await Promise.all([
        tomiService.getStatus().catch(() => null),
        tomiService.getPositions().catch(() => ({ positions: [], balance: null, count: 0 })),
        tomiService.getTrades(100).catch(() => null),
      ]);
      if (statusRes) setStatus(statusRes);
      setPositions(posRes.positions);
      if (tradeRes) setTradeData(tradeRes);
    } catch (err) {
      setError('Failed to load Tomi dashboard data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAll();
    const interval = setInterval(fetchAll, 30000); // refresh every 30s
    return () => clearInterval(interval);
  }, [fetchAll]);

  // ─── TradingView Chart Init ──────────────────────────

  useEffect(() => {
    if (tab !== 3) return; // Only init when chart tab is active

    let widget: any = null;
    let mounted = true;

    const initChart = async () => {
      if (!chartContainerRef.current) return;

      // Load TradingView library if not already loaded
      if (!(window as any).TradingView) {
        try {
          await new Promise<void>((resolve, reject) => {
            const script = document.createElement('script');
            script.src = '/charting_library/charting_library.js';
            script.onload = () => resolve();
            script.onerror = () => reject(new Error('TradingView library not found'));
            document.head.appendChild(script);
          });
        } catch {
          return;
        }
      }

      if (!mounted || !(window as any).TradingView) return;

      // Clean up existing widget
      if (tvWidgetRef.current) {
        try { tvWidgetRef.current.remove(); } catch { /* noop */ }
        tvWidgetRef.current = null;
      }

      const containerId = `tomi_tv_chart_${Date.now()}`;
      chartContainerRef.current.innerHTML = '';
      const div = document.createElement('div');
      div.id = containerId;
      div.style.cssText = 'width:100%;height:500px;';
      chartContainerRef.current.appendChild(div);

      requestAnimationFrame(() => {
        if (!mounted || !document.getElementById(containerId)) return;

        try {
          widget = new (window as any).TradingView.widget({
            container: containerId,
            datafeed: createCoinbaseDatafeed(),
            library_path: '/charting_library/',
            symbol: 'COINBASE:BTCUSD',
            autosize: true,
            interval: '240',
            timezone: 'Etc/UTC',
            theme: 'Dark',
            locale: 'en',
            toolbar_bg: '#1a1a2e',
            enable_publishing: false,
            hide_side_toolbar: false,
            allow_symbol_change: true,
            details: true,
            studies_overrides: {},
            overrides: {},
            loading_screen: { backgroundColor: '#0d1117' },
            disabled_features: [
              'use_localstorage_for_settings',
              'save_chart_properties_to_local_storage',
              'header_saveload',
              'study_templates',
            ],
            enabled_features: ['study_templates'],
            save_load_adapter: {
              getAllCharts: () => Promise.resolve([]),
              removeChart: () => Promise.resolve(),
              saveChart: () => Promise.resolve(),
              getChartContent: () => Promise.resolve(''),
              getAllStudyTemplates: () => Promise.resolve([]),
              removeStudyTemplate: () => Promise.resolve(),
              saveStudyTemplate: () => Promise.resolve(),
              getStudyTemplateContent: () => Promise.resolve(''),
            },
          });
          tvWidgetRef.current = widget;
        } catch (err) {
          console.error('TradingView init failed:', err);
        }
      });
    };

    initChart();

    return () => {
      mounted = false;
      if (widget) {
        try { widget.remove(); } catch { /* noop */ }
      }
    };
  }, [tab]);

  // ─── DCA Analysis ────────────────────────────────────

  const handleRunDca = async () => {
    setDcaLoading(true);
    try {
      const result = await tomiService.analyzeDca(dcaForm);
      setDcaResult(result);
    } catch {
      setError('Failed to run DCA strategy analysis');
    } finally {
      setDcaLoading(false);
    }
  };

  // ─── Order Submission ────────────────────────────────

  const handlePlaceOrder = async () => {
    if (!orderForm.size) return;
    setSubmitting(true);
    try {
      await tomiService.placeOrder(orderForm);
      setOrderDialogOpen(false);
      setOrderForm({ ...orderForm, size: '', limit_price: '' });
      fetchAll();
    } catch (err) {
      setError('Failed to place order');
    } finally {
      setSubmitting(false);
    }
  };

  const handleResetCounter = async () => {
    try {
      await tomiService.resetTradeCounter();
      setResetDialogOpen(false);
      fetchAll();
    } catch (err) {
      setError('Failed to reset counter');
    }
  };

  const handleCancelOrder = async (orderId: string) => {
    try {
      await tomiService.cancelOrder(orderId);
      fetchAll();
    } catch (err) {
      setError('Failed to cancel order');
    }
  };

  // ─── Derived ──────────────────────────────────────────

  const connected = status?.coinbase?.connected ?? false;
  const dryRun = status?.coinbase?.dryRun ?? true;
  const tradeCount = status?.coinbase?.tradeCount ?? 0;
  const maxTrades = status?.coinbase?.maxTrades ?? 100;
  const remaining = status?.coinbase?.remaining ?? 100;
  const tradeProgress = (tradeCount / maxTrades) * 100;
  const bal = status?.futuresBalance;

  // ─── Render ───────────────────────────────────────────

  if (loading && !status) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <CurrencyBitcoin sx={{ fontSize: 36, color: theme.palette.primary.main }} />
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 700 }}>
              富の神 Tomi-no-Kami
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Quantitative Futures Trading Engine &bull; Coinbase Advanced
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <Chip
            icon={<StatusDot ok={connected} />}
            label={connected ? 'LIVE' : dryRun ? 'DRY RUN' : 'DISCONNECTED'}
            variant="outlined"
            color={connected ? 'success' : 'warning'}
          />
          <IconButton onClick={fetchAll} title="Refresh">
            <Refresh />
          </IconButton>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Top Stats Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {/* Trade Counter */}
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography variant="overline" color="text.secondary">
                Trade Counter
              </Typography>
              <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
                {tradeCount} / {maxTrades}
              </Typography>
              <LinearProgress
                variant="determinate"
                value={tradeProgress}
                sx={{
                  height: 8,
                  borderRadius: 4,
                  mb: 1,
                  '& .MuiLinearProgress-bar': {
                    backgroundColor: tradeProgress > 80 ? '#f44336' : tradeProgress > 50 ? '#ff9800' : '#4caf50',
                  },
                }}
              />
              <Typography variant="caption" color="text.secondary">
                {remaining} trades remaining
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* Buying Power */}
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography variant="overline" color="text.secondary">
                Futures Buying Power
              </Typography>
              <Typography variant="h4" sx={{ fontWeight: 700 }}>
                {formatUsd(bal?.futures_buying_power)}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Available margin: {formatUsd(bal?.available_margin)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* Unrealized PnL */}
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography variant="overline" color="text.secondary">
                Unrealized P&L
              </Typography>
              <Typography
                variant="h4"
                sx={{ fontWeight: 700, color: pnlColor(bal?.unrealized_pnl) }}
              >
                {formatUsd(bal?.unrealized_pnl)}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Daily realized: {formatUsd(bal?.daily_realized_pnl)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* Total Balance */}
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography variant="overline" color="text.secondary">
                Total USD Balance
              </Typography>
              <Typography variant="h4" sx={{ fontWeight: 700 }}>
                {formatUsd(bal?.total_usd_balance)}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Liq. buffer: {bal?.liquidation_buffer_percentage ?? '0'}%
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Action Buttons */}
      <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
        <Button
          variant="contained"
          startIcon={<Send />}
          onClick={() => setOrderDialogOpen(true)}
          disabled={remaining <= 0}
        >
          New Futures Order
        </Button>
        <Button
          variant="outlined"
          color="warning"
          onClick={() => setResetDialogOpen(true)}
          size="small"
        >
          Reset Counter
        </Button>
      </Box>

      {/* Tabs */}
      <Paper sx={{ mb: 3 }}>
        <Tabs
          value={tab}
          onChange={(_e, v) => setTab(v)}
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tab icon={<MonitorHeart />} iconPosition="start" label="Positions" />
          <Tab icon={<History />} iconPosition="start" label="Trade History" />
          <Tab icon={<AccountBalance />} iconPosition="start" label="Balance Details" />
          <Tab icon={<ShowChart />} iconPosition="start" label="Chart" />
          <Tab icon={<Analytics />} iconPosition="start" label="DCA Strategy" />
          <Tab icon={<Forum />} iconPosition="start" label="富↔風 Chat" />
        </Tabs>
      </Paper>

      {/* Tab: Positions */}
      <TabPanel value={tab} index={0}>
        {positions.length === 0 ? (
          <Alert severity="info">No open futures positions</Alert>
        ) : (
          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Product</TableCell>
                  <TableCell>Side</TableCell>
                  <TableCell align="right">Contracts</TableCell>
                  <TableCell align="right">Avg Entry</TableCell>
                  <TableCell align="right">Current Price</TableCell>
                  <TableCell align="right">Unrealized P&L</TableCell>
                  <TableCell>Expiry</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {positions.map((pos, idx) => (
                  <TableRow key={idx}>
                    <TableCell sx={{ fontWeight: 600 }}>{pos.product_id}</TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={pos.side}
                        icon={pos.side === 'LONG' ? <TrendingUp /> : <TrendingDown />}
                        color={pos.side === 'LONG' ? 'success' : 'error'}
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell align="right">{pos.number_of_contracts}</TableCell>
                    <TableCell align="right">{formatUsd(pos.avg_entry_price)}</TableCell>
                    <TableCell align="right">{formatUsd(pos.current_price)}</TableCell>
                    <TableCell
                      align="right"
                      sx={{ color: pnlColor(pos.unrealized_pnl), fontWeight: 600 }}
                    >
                      {formatUsd(pos.unrealized_pnl)}
                    </TableCell>
                    <TableCell>{pos.expiry || '—'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </TabPanel>

      {/* Tab: Trade History */}
      <TabPanel value={tab} index={1}>
        {!tradeData || (tradeData.localTradeLog.length === 0 && tradeData.coinbaseOrders.length === 0) ? (
          <Alert severity="info">No trades recorded yet</Alert>
        ) : (
          <>
            {/* Local Tomi Trade Log */}
            {tradeData.localTradeLog.length > 0 && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
                  Tomi Engine Trades ({tradeData.localTradeLog.length})
                </Typography>
                <TableContainer component={Paper}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Time</TableCell>
                        <TableCell>Product</TableCell>
                        <TableCell>Side</TableCell>
                        <TableCell align="right">Size</TableCell>
                        <TableCell align="right">Price</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Order ID</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {tradeData.localTradeLog.map((t) => (
                        <TableRow key={t.id}>
                          <TableCell sx={{ fontSize: '0.8rem' }}>
                            {new Date(t.timestamp).toLocaleString()}
                          </TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>{t.product_id}</TableCell>
                          <TableCell>
                            <Chip
                              size="small"
                              label={t.side}
                              color={t.side === 'BUY' ? 'success' : 'error'}
                              variant="outlined"
                            />
                          </TableCell>
                          <TableCell align="right">{t.size}</TableCell>
                          <TableCell align="right">{t.price}</TableCell>
                          <TableCell>{t.status}</TableCell>
                          <TableCell sx={{ fontSize: '0.75rem', fontFamily: 'monospace' }}>
                            {t.order_id.slice(0, 12)}…
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            )}

            {/* Coinbase Order History */}
            {tradeData.coinbaseOrders.length > 0 && (
              <Box>
                <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
                  Coinbase Futures Orders ({tradeData.coinbaseOrders.length})
                </Typography>
                <TableContainer component={Paper}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Time</TableCell>
                        <TableCell>Product</TableCell>
                        <TableCell>Side</TableCell>
                        <TableCell>Type</TableCell>
                        <TableCell align="right">Filled</TableCell>
                        <TableCell align="right">Avg Price</TableCell>
                        <TableCell align="right">Value</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {tradeData.coinbaseOrders.map((o) => (
                        <TableRow key={o.order_id}>
                          <TableCell sx={{ fontSize: '0.8rem' }}>
                            {new Date(o.created_time).toLocaleString()}
                          </TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>{o.product_id}</TableCell>
                          <TableCell>
                            <Chip
                              size="small"
                              label={o.side}
                              color={o.side === 'BUY' ? 'success' : 'error'}
                              variant="outlined"
                            />
                          </TableCell>
                          <TableCell>{o.order_type}</TableCell>
                          <TableCell align="right">{o.filled_size}</TableCell>
                          <TableCell align="right">{formatUsd(o.average_filled_price)}</TableCell>
                          <TableCell align="right">{formatUsd(o.total_value_after_fees)}</TableCell>
                          <TableCell>
                            <Chip
                              size="small"
                              label={o.status}
                              icon={
                                o.status === 'FILLED' ? <CheckCircle /> :
                                o.status === 'CANCELLED' ? <Cancel /> :
                                <Warning />
                              }
                              color={
                                o.status === 'FILLED' ? 'success' :
                                o.status === 'CANCELLED' ? 'default' :
                                'warning'
                              }
                              variant="outlined"
                            />
                          </TableCell>
                          <TableCell>
                            {o.status === 'OPEN' || o.status === 'PENDING' ? (
                              <Tooltip title="Cancel order">
                                <IconButton
                                  size="small"
                                  color="error"
                                  onClick={() => handleCancelOrder(o.order_id)}
                                >
                                  <Cancel fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            ) : null}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            )}
          </>
        )}
      </TabPanel>

      {/* Tab: Balance Details */}
      <TabPanel value={tab} index={2}>
        {!bal ? (
          <Alert severity="info">
            {dryRun ? 'Dry-run mode — no live balance data' : 'No balance data available'}
          </Alert>
        ) : (
          <Grid container spacing={2}>
            {[
              { label: 'Total USD Balance', value: bal.total_usd_balance },
              { label: 'Futures Buying Power', value: bal.futures_buying_power },
              { label: 'CBI USD Balance', value: bal.cbi_usd_balance },
              { label: 'CFM USD Balance', value: bal.cfm_usd_balance },
              { label: 'Initial Margin', value: bal.initial_margin },
              { label: 'Available Margin', value: bal.available_margin },
              { label: 'Open Orders Hold', value: bal.total_open_orders_hold_amount },
              { label: 'Unrealized P&L', value: bal.unrealized_pnl, pnl: true },
              { label: 'Daily Realized P&L', value: bal.daily_realized_pnl, pnl: true },
              { label: 'Liquidation Threshold', value: bal.liquidation_threshold },
              { label: 'Liquidation Buffer $', value: bal.liquidation_buffer_amount },
              { label: 'Liquidation Buffer %', value: bal.liquidation_buffer_percentage, raw: true },
            ].map((item) => (
              <Grid item xs={12} sm={6} md={4} key={item.label}>
                <Card variant="outlined">
                  <CardContent sx={{ py: 1.5 }}>
                    <Typography variant="caption" color="text.secondary">
                      {item.label}
                    </Typography>
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 600,
                        color: item.pnl ? pnlColor(item.value) : 'inherit',
                      }}
                    >
                      {item.raw ? `${item.value}%` : formatUsd(item.value)}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}
      </TabPanel>

      {/* Tab: TradingView Chart */}
      <TabPanel value={tab} index={3}>
        <Paper sx={{ p: 2, bgcolor: '#0d1117', borderRadius: 2 }}>
          <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600, color: '#e6edf3' }}>
            BTC-USD Live Chart — TradingView
          </Typography>
          <Box ref={chartContainerRef} sx={{ width: '100%', height: 500, borderRadius: 1, overflow: 'hidden' }}>
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
              <CircularProgress size={32} />
              <Typography sx={{ ml: 2, color: '#8b949e' }}>Loading chart…</Typography>
            </Box>
          </Box>
          <Typography variant="caption" sx={{ color: '#8b949e', mt: 1, display: 'block' }}>
            Powered by TradingView Charting Library v29 • Data: Coinbase Exchange API • Auto-updates every 5s
          </Typography>
        </Paper>
      </TabPanel>

      {/* Tab: DCA Strategy Analyzer */}
      <TabPanel value={tab} index={4}>
        <Grid container spacing={3}>
          {/* DCA Configuration */}
          <Grid item xs={12} md={4}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
                Strategy Parameters
              </Typography>

              <TextField
                fullWidth size="small" label="Initial Capital ($)" type="number"
                value={dcaForm.capital}
                onChange={(e) => setDcaForm({ ...dcaForm, capital: Number(e.target.value) })}
                sx={{ mb: 2 }}
                inputProps={{ min: 100, step: 1000 }}
              />
              <TextField
                fullWidth size="small" label="BTC Current Price ($)" type="number"
                value={dcaForm.current_price}
                onChange={(e) => setDcaForm({ ...dcaForm, current_price: Number(e.target.value) })}
                sx={{ mb: 2 }}
                inputProps={{ min: 1000, step: 1000 }}
              />
              <TextField
                fullWidth size="small" label="Target Exit Price ($)" type="number"
                value={dcaForm.target_price}
                onChange={(e) => setDcaForm({ ...dcaForm, target_price: Number(e.target.value) })}
                sx={{ mb: 2 }}
                inputProps={{ min: 1000, step: 5000 }}
              />

              <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                DCA Tranches: {dcaForm.tranches}
              </Typography>
              <Slider
                value={dcaForm.tranches}
                onChange={(_e, v) => setDcaForm({ ...dcaForm, tranches: v as number })}
                min={2} max={50} step={1}
                valueLabelDisplay="auto"
                sx={{ mb: 2 }}
              />

              <TextField
                fullWidth size="small" label="Contracts per Tranche" type="number"
                value={dcaForm.contracts_per_tranche}
                onChange={(e) => setDcaForm({ ...dcaForm, contracts_per_tranche: Number(e.target.value) })}
                sx={{ mb: 2 }}
                inputProps={{ min: 1, step: 1 }}
              />

              <FormControl fullWidth size="small" sx={{ mb: 3 }}>
                <InputLabel>Product</InputLabel>
                <Select
                  value={dcaForm.product_id}
                  label="Product"
                  onChange={(e) => setDcaForm({ ...dcaForm, product_id: e.target.value })}
                >
                  <MenuItem value="BTC-USD">BTC-USD</MenuItem>
                  <MenuItem value="BIT-28MAR25-CDE">BIT-28MAR25-CDE</MenuItem>
                  <MenuItem value="BIT-25APR25-CDE">BIT-25APR25-CDE</MenuItem>
                  <MenuItem value="BIT-30MAY25-CDE">BIT-30MAY25-CDE</MenuItem>
                </Select>
              </FormControl>

              <Button
                fullWidth variant="contained" onClick={handleRunDca}
                disabled={dcaLoading}
                startIcon={dcaLoading ? <CircularProgress size={18} /> : <Analytics />}
                sx={{ py: 1.5 }}
              >
                {dcaLoading ? 'Analyzing…' : 'Run 富の神 Analysis'}
              </Button>
            </Paper>
          </Grid>

          {/* DCA Results */}
          <Grid item xs={12} md={8}>
            {!dcaResult ? (
              <Paper sx={{ p: 4, textAlign: 'center' }}>
                <Analytics sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
                <Typography color="text.secondary">
                  Configure your DCA strategy parameters and run the analysis to see projections
                  based on your historical Coinbase trading performance.
                </Typography>
              </Paper>
            ) : (
              <Box>
                {/* Verdict */}
                <Alert
                  severity={dcaResult.recommendation.verdict === 'FAVORABLE' ? 'success' : 'warning'}
                  sx={{ mb: 2 }}
                  icon={dcaResult.recommendation.verdict === 'FAVORABLE' ? <CheckCircle /> : <Warning />}
                >
                  <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                    富の神 Verdict: {dcaResult.recommendation.verdict}
                  </Typography>
                  <Typography variant="body2">{dcaResult.recommendation.rationale}</Typography>
                </Alert>

                {/* Strategy Stats Cards */}
                <Grid container spacing={2} sx={{ mb: 2 }}>
                  {[
                    { label: 'Projected Net P&L', value: formatUsd(dcaResult.strategy.projected_net_pnl), color: pnlColor(dcaResult.strategy.projected_net_pnl) },
                    { label: 'Projected ROI', value: `${dcaResult.strategy.projected_roi_pct}%`, color: pnlColor(dcaResult.strategy.projected_roi_pct) },
                    { label: 'Upside to Target', value: `${dcaResult.strategy.upside_pct}%`, color: '#4caf50' },
                    { label: 'Avg Entry Price', value: formatUsd(dcaResult.strategy.avg_entry_price), color: 'inherit' },
                    { label: 'Total Contracts', value: String(dcaResult.strategy.total_contracts), color: 'inherit' },
                    { label: 'Liquidation Price', value: formatUsd(dcaResult.strategy.liquidation_price), color: '#f44336' },
                  ].map((item) => (
                    <Grid item xs={6} sm={4} key={item.label}>
                      <Card variant="outlined">
                        <CardContent sx={{ py: 1.5, textAlign: 'center' }}>
                          <Typography variant="caption" color="text.secondary">{item.label}</Typography>
                          <Typography variant="h6" sx={{ fontWeight: 700, color: item.color }}>
                            {item.value}
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>

                {/* Historical Performance */}
                <Paper sx={{ p: 2, mb: 2 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                    Historical Performance (basis for projections)
                  </Typography>
                  <Grid container spacing={2}>
                    {[
                      { label: 'Win Rate', value: `${dcaResult.historical.win_rate}%` },
                      { label: 'BTC Win Rate', value: `${dcaResult.historical.btc_win_rate}%` },
                      { label: 'Profit Factor', value: String(dcaResult.historical.profit_factor) },
                      { label: 'Expectancy', value: `$${dcaResult.historical.expectancy_per_trade}/trade` },
                      { label: 'Net P&L (hist)', value: formatUsd(dcaResult.historical.total_net_pnl) },
                      { label: 'Total Volume', value: formatUsd(dcaResult.historical.total_volume) },
                    ].map((item) => (
                      <Grid item xs={6} sm={4} md={2} key={item.label}>
                        <Typography variant="caption" color="text.secondary" display="block">{item.label}</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>{item.value}</Typography>
                      </Grid>
                    ))}
                  </Grid>
                </Paper>

                {/* Scenarios Table */}
                <Paper sx={{ p: 2, mb: 2 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                    Price Scenarios
                  </Typography>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Scenario</TableCell>
                          <TableCell align="right">BTC Price</TableCell>
                          <TableCell align="right">Projected P&L</TableCell>
                          <TableCell align="right">ROI</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {dcaResult.strategy.scenarios.map((s) => (
                          <TableRow key={s.label}>
                            <TableCell sx={{ fontWeight: 600 }}>{s.label}</TableCell>
                            <TableCell align="right">{formatUsd(s.price)}</TableCell>
                            <TableCell align="right" sx={{ color: pnlColor(s.pnl), fontWeight: 600 }}>
                              {formatUsd(s.pnl)}
                            </TableCell>
                            <TableCell align="right" sx={{ color: pnlColor(s.pnl) }}>
                              {dcaForm.capital > 0 ? `${((s.pnl / dcaForm.capital) * 100).toFixed(1)}%` : '—'}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Paper>

                {/* DCA Levels */}
                <Paper sx={{ p: 2, mb: 2 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                    DCA Entry Levels ({dcaResult.strategy.tranches} tranches)
                  </Typography>
                  <TableContainer sx={{ maxHeight: 300 }}>
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell>#</TableCell>
                          <TableCell align="right">Entry Price</TableCell>
                          <TableCell align="right">Contracts</TableCell>
                          <TableCell align="right">Capital</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {dcaResult.strategy.dca_levels.map((l) => (
                          <TableRow key={l.tranche}>
                            <TableCell>{l.tranche}</TableCell>
                            <TableCell align="right">{formatUsd(l.entry_price)}</TableCell>
                            <TableCell align="right">{l.contracts}</TableCell>
                            <TableCell align="right">{formatUsd(l.capital_deployed)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Paper>

                {/* Risk Factors */}
                {dcaResult.recommendation.risk_factors.length > 0 && (
                  <Alert severity="warning">
                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Risk Factors</Typography>
                    {dcaResult.recommendation.risk_factors.map((r, i) => (
                      <Typography key={i} variant="body2">• {r}</Typography>
                    ))}
                  </Alert>
                )}
              </Box>
            )}
          </Grid>
        </Grid>
      </TabPanel>

      {/* Tab: Agent Chat */}
      <TabPanel value={tab} index={5}>
        <AgentChatPanel
          perspective="tomi"
          getChat={() => tomiService.getChat()}
          sendChat={(msg) => tomiService.sendChat(msg)}
          triggerExchange={(init) => tomiService.triggerChatExchange(init)}
          clearChat={() => tomiService.clearChat()}
        />
      </TabPanel>

      {/* ─── Order Dialog ──────────────────────────────── */}
      <Dialog
        open={orderDialogOpen}
        onClose={() => setOrderDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Place Futures Order</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12}>
              <FormControl fullWidth size="small">
                <InputLabel>Product</InputLabel>
                <Select
                  value={orderForm.product_id}
                  label="Product"
                  onChange={(e) => setOrderForm({ ...orderForm, product_id: e.target.value })}
                >
                  {FUTURES_PRODUCTS.map((p) => (
                    <MenuItem key={p} value={p}>{p}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Side</InputLabel>
                <Select
                  value={orderForm.side}
                  label="Side"
                  onChange={(e) => setOrderForm({ ...orderForm, side: e.target.value as 'BUY' | 'SELL' })}
                >
                  <MenuItem value="BUY">BUY (Long)</MenuItem>
                  <MenuItem value="SELL">SELL (Short)</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Order Type</InputLabel>
                <Select
                  value={orderForm.order_type}
                  label="Order Type"
                  onChange={(e) => setOrderForm({ ...orderForm, order_type: e.target.value as 'market' | 'limit' })}
                >
                  <MenuItem value="market">Market</MenuItem>
                  <MenuItem value="limit">Limit</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                size="small"
                label="Size (USD for market / contracts for limit)"
                value={orderForm.size}
                onChange={(e) => setOrderForm({ ...orderForm, size: e.target.value })}
                type="number"
                inputProps={{ min: 0, step: 'any' }}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                size="small"
                label="Leverage"
                value={orderForm.leverage}
                onChange={(e) => setOrderForm({ ...orderForm, leverage: e.target.value })}
                type="number"
                inputProps={{ min: 1, max: 20 }}
              />
            </Grid>
            {orderForm.order_type === 'limit' && (
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  size="small"
                  label="Limit Price (USD)"
                  value={orderForm.limit_price}
                  onChange={(e) => setOrderForm({ ...orderForm, limit_price: e.target.value })}
                  type="number"
                  inputProps={{ min: 0, step: 'any' }}
                />
              </Grid>
            )}
          </Grid>
          {remaining <= 5 && remaining > 0 && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              Only {remaining} trades remaining in the 100-trade allocation
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOrderDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handlePlaceOrder}
            disabled={submitting || !orderForm.size}
            color={orderForm.side === 'BUY' ? 'success' : 'error'}
          >
            {submitting ? <CircularProgress size={20} /> : `${orderForm.side} ${orderForm.product_id}`}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ─── Reset Counter Dialog ──────────────────────── */}
      <Dialog
        open={resetDialogOpen}
        onClose={() => setResetDialogOpen(false)}
        maxWidth="xs"
      >
        <DialogTitle>Reset Trade Counter?</DialogTitle>
        <DialogContent>
          <Typography>
            This will reset the 100-trade counter back to 0 and clear the local trade log.
            Current count: <strong>{tradeCount}/{maxTrades}</strong>
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResetDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" color="warning" onClick={handleResetCounter}>
            Reset
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
